import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { PlayerTableViewModelV2 } from '../api-responses';
import { GameTableState } from '../types/game-table';
import {
  ChangeSeatBody,
  ErrorBody,
  FillSeatBody,
  MakeBidBody,
  MeResponse,
  OpenGameSchema,
  OpenTableBody,
  PlayCardBody,
  RegisterBody,
  RegisterResponse,
  SetTableLockBody,
  StartGameBody,
  SuccessBody,
  TableCodeParams,
  TableIdParams,
} from './schemas';

const c = initContract();

// Single source of truth for the REST API, shared by the Express server
// (@ts-rest/express) and the client (@ts-rest/core initClient). Request bodies
// are zod-validated automatically; the two large response bodies reuse the
// existing view-model TS types via c.type<>() rather than duplicating them as
// zod schemas.
export const contract = c.router(
  {
    getMe: {
      method: 'GET',
      path: '/me',
      responses: {
        200: MeResponse,
        401: ErrorBody,
      },
      summary: 'Current user + whether they are seated at a table',
    },

    register: {
      method: 'POST',
      path: '/register',
      body: RegisterBody,
      responses: {
        200: RegisterResponse,
        400: ErrorBody,
      },
      summary: 'Create a user and set the AuthToken cookie',
    },

    logout: {
      method: 'POST',
      path: '/logout',
      body: c.noBody(),
      responses: {
        200: SuccessBody,
        401: ErrorBody,
      },
      summary: "Revoke the caller's token and clear the AuthToken cookie",
    },

    getOpenGames: {
      method: 'GET',
      path: '/v2/opengames',
      responses: {
        200: z.array(OpenGameSchema),
        401: ErrorBody,
      },
      summary: 'List tables awaiting players',
    },

    openTable: {
      method: 'POST',
      path: '/v2/openTable',
      body: OpenTableBody,
      responses: {
        200: c.type<GameTableState>(),
        401: ErrorBody,
        503: ErrorBody,
      },
      summary: 'Open a new table (with score/privacy config) and seat the creator',
    },

    joinTable: {
      method: 'POST',
      path: '/v2/tables/:id/join',
      pathParams: TableIdParams,
      body: c.noBody(),
      responses: {
        200: SuccessBody,
        400: ErrorBody,
        401: ErrorBody,
        404: ErrorBody,
      },
      summary: 'Join an existing table',
    },

    joinTableByCode: {
      method: 'POST',
      path: '/v2/tables/code/:code/join',
      pathParams: TableCodeParams,
      body: c.noBody(),
      responses: {
        200: SuccessBody,
        400: ErrorBody,
        401: ErrorBody,
        404: ErrorBody,
      },
      summary: 'Join a table via its invite code',
    },

    getGame: {
      method: 'GET',
      path: '/v2/game',
      responses: {
        200: c.type<PlayerTableViewModelV2>(),
        401: ErrorBody,
        404: ErrorBody,
      },
      summary: 'Player-scoped view of the table the caller is seated at',
    },

    startGame: {
      method: 'POST',
      path: '/v2/startGame',
      body: StartGameBody,
      responses: {
        200: SuccessBody,
        400: ErrorBody,
        401: ErrorBody,
        404: ErrorBody,
      },
      summary: 'Start the game at a preparing table',
    },

    changeSeat: {
      method: 'POST',
      path: '/v2/changeSeat',
      body: ChangeSeatBody,
      responses: {
        200: SuccessBody,
        400: ErrorBody,
        401: ErrorBody,
        404: ErrorBody,
      },
      summary: 'Move to an open seat before the game starts',
    },

    sitDown: {
      method: 'POST',
      path: '/v2/sitDown',
      body: ChangeSeatBody,
      responses: {
        200: SuccessBody,
        400: ErrorBody,
        401: ErrorBody,
        404: ErrorBody,
      },
      summary: 'Spectator takes an available seat to (re)join the table',
    },

    makeBid: {
      method: 'POST',
      path: '/v2/makeBid',
      body: MakeBidBody,
      responses: {
        200: SuccessBody,
        400: ErrorBody,
        401: ErrorBody,
        404: ErrorBody,
      },
      summary: 'Submit a bid',
    },

    playCard: {
      method: 'POST',
      path: '/v2/playCard',
      body: PlayCardBody,
      responses: {
        200: SuccessBody,
        400: ErrorBody,
        401: ErrorBody,
        404: ErrorBody,
      },
      summary: "Play a card from the caller's hand",
    },

    playerReady: {
      method: 'POST',
      path: '/v2/playerReady',
      body: c.noBody(),
      responses: {
        200: SuccessBody,
        400: ErrorBody,
        401: ErrorBody,
        404: ErrorBody,
      },
      summary: 'Mark the caller ready for a new game after game over',
    },

    leaveTable: {
      method: 'POST',
      path: '/v2/leaveTable',
      body: c.noBody(),
      responses: {
        200: SuccessBody,
        401: ErrorBody,
        404: ErrorBody,
      },
      summary: 'Leave the table the caller is seated at',
    },

    fillSeatWithComputer: {
      method: 'POST',
      path: '/v2/fillSeatWithComputer',
      body: FillSeatBody,
      responses: {
        200: SuccessBody,
        400: ErrorBody,
        401: ErrorBody,
        404: ErrorBody,
      },
      summary: 'Host fills a vacant (paused) seat with a computer and resumes',
    },

    setTableLock: {
      method: 'POST',
      path: '/v2/setTableLock',
      body: SetTableLockBody,
      responses: {
        200: SuccessBody,
        400: ErrorBody,
        401: ErrorBody,
        404: ErrorBody,
      },
      summary: 'Host locks/unlocks the table (blocks new players; admits spectators)',
    },

    spectate: {
      method: 'POST',
      path: '/v2/spectate',
      body: c.noBody(),
      responses: {
        200: SuccessBody,
        400: ErrorBody,
        401: ErrorBody,
        404: ErrorBody,
      },
      summary: 'Give up your seat before the game starts and watch as a spectator',
    },
  },
  {
    strictStatusCodes: true,
  },
);
